import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

//Components
import { NotificationManager } from 'Components/Notifications';
import { IconButton, MenuComponent, TableButtons, TableView } from '@gpstrackit/gtc-react-controls';
import Slider from "Components/Slider";
import Button from "../../../../components/Button";
import { Grid } from '@mui/material';

//Hooks
import { usePaggination } from "Core/hooks";
import {
    COLORS_SLIDERS_SCORE,
    METRIC_SPEED_KEY,
    METRIC_SPEED_LABEL,
    METRIC_SPEED_OVER_KEY,
    METRIC_SPEED_OVER_LABEL_HIGH,
    METRIC_SPEED_OVER_LABEL_LIST,
    METRIC_SPEED_OVER_LABEL_LOW,
    METRIC_SPEED_OVER_LABEL_MEDIUM,
    useMetricTypesOptions
} from "Modules/settings/views/behavior/redux/helpers";
//Actions
import { clearRedux, sendGenericExport } from 'Redux/actions';

//Utils
import { HasPermission } from "Core/security"

//Views
import CreateUpdateMetric from "./modals/create";
import { searchBehaviorPoints } from "./redux/slice";
import Preferences from "Core/containers/Preferences";

//Slice
import { openModalCreateUpdateMetricService } from "Modules/settings/views/behavior/modals/create/redux/slice";
import { openModalDeleteMetricService } from "Modules/settings/views/behavior/modals/delete/redux/slice";
import { openModalBehaviorAdvanceFilter } from "Modules/settings/views/behavior/modals/filter/redux/slice";

//Styles
import './styles.scss';
import DeleteMetric from "Modules/settings/views/behavior/modals/delete";
import AdvanceFilterMetric from "Modules/settings/views/behavior/modals/filter";
import { convertToKPH, MILES_TO_KPH } from "Core/data/Helpers";

const BehaviorConfigView = (props) => {
    const { messages, locale } = useIntl();
    const tableRef = useRef();
    const preferencesRef = useRef();
    const dispatch = useDispatch();

    //Local States
    const user = useSelector((state) => state.securityRedux.user);
    const [scores, setScores] = useState([25, 50]);
    const [movedSlider, setMovedSlider] = useState(false);
    const [selectedItems, setSelectedItems] = useState([]);

    //Redux States
    const openModalCreateUpdateMetric = useSelector(state => state.manageCreateUpdateMetricServiceRedux.open);
    const openModalDeleteMetric = useSelector(state => state.manageDeleteMetricServiceRedux.open);
    const exportResult = useSelector(state => state.commomRedux.exportResult);
    const exportName = useSelector(state => state.commomRedux.exportName);
    const {
        open: openModalAdvanceFilterMetric,
        hasFilters,
        filter
    } = useSelector(state => state.manageBehaviorAdvanceFilterRedux);
    const {
        loading,
        items,
        total
    } = useSelector(state => state.manageSearchBehaviorPointsRedux);

    //Hook
    const pagination = usePaggination(searchBehaviorPoints, ['metric', 'behaviorMetric_category'], false, tableRef)
    const { menuMetricOptions, getNameMetricType, getMetricTooltip } = useMetricTypesOptions(user.isKph, messages);

    // Permissions
    const editablePermissions = HasPermission(user, "", [349])

    useEffect(() => {
        if (movedSlider) {
            // save user preference with small timeout for performance
            const timer = updateGradesRequest();
            return () => clearTimeout(timer);
        }
    }, [JSON.stringify(scores)]);

    useEffect(() => {
        pagination.setColumns([
            {
                key: 'behaviorMetric_category',
                title: messages['behavior_category'],
                show: true,
                export: true,
                sort: true,
                width: 250,
                render: (row) => {
                    return (
                        <Grid container alignItems="center" justifyContent="space-between">
                            <Grid item xs={9} style={{ width: 200 }}>
                                <span>{messages['behavior_' + row.category]}</span>
                            </Grid>
                            {editablePermissions &&
                                <Grid item xs={3}>
                                    <MenuComponent
                                        disabled={loading}
                                        options={menuMetricOptions}
                                        icon="more_vert"
                                        className="showInHover"
                                        onClick={(type, metric) => {
                                            if (type === messages["edit"]) {
                                                dispatch(openModalCreateUpdateMetricService(metric))
                                            }
                                            if (type === messages["delete"]) {
                                                dispatch(openModalDeleteMetricService([metric]))
                                            }
                                        }}
                                        value={row}
                                    />
                                </Grid>
                            }
                        </Grid>
                    )
                },
                exportValue: (row) => messages['behavior_' + row.category],
                functionString: `
                    const category = {
                        aggression: '${messages['behavior_aggression']}',
                        driverMonitoringSystem: '${messages['behavior_driverMonitoringSystem']}',
                        advancedDriverAssistanceSystem: '${messages['behavior_advancedDriverAssistanceSystem']}',
                        vehicleSpeed: '${messages['behavior_vehicleSpeed']}',
                        speedingOverPosted: '${messages['behavior_speedingOverPosted']}',
                    }
                    
                    return category[row?.behaviorMetric?.category] || row?.behaviorMetric?.category
                `
            },
            {
                key: 'metric',
                title: messages['behavior_metric'],
                show: true,
                export: true,
                sort: true,
                width: 250,
                render: (row) => {
                    return (
                        <span>
                            {
                                row.category === 'vehicleSpeed' && (
                                    <>
                                        {`${messages['behavior_' + row.metric]} ${row.metricValue ? (user.isKph ? convertToKPH(row.metricValue) : row.metricValue) : ''}`}
                                        <Button
                                            type="icon"
                                            icon="info"
                                            style={{ fontSize: '15px' }}
                                            tooltip={row.metricValue === 100 ?
                                                messages['behavior_tooltip_speedLabelFinal' + (user.isKph ? 'Kph' : '')] : `${messages['behavior_tooltip_speedLabel']} ${row.metricValue ?
                                                    (user.isKph ? convertToKPH(row.metricValue) : row.metricValue) : ''} ${messages['behavior_tooltip_speedLabelComplement']} ${row.metricValue ?
                                                        (user.isKph ? convertToKPH(row.metricValue + 5) : row.metricValue + 5) : ''}`}
                                            onClick={() => {
                                            }}
                                            className="icon-weights-info"
                                        />
                                    </>
                                )
                            }
                            {
                                !['vehicleSpeed', 'speedingOverPosted'].includes(row.category) && (
                                    <>
                                        {getNameMetricType(row)}
                                        <Button
                                            type="icon"
                                            icon="info"
                                            style={{ fontSize: '15px' }}
                                            tooltip={getMetricTooltip(row.metric)}
                                            onClick={() => {
                                            }}
                                            className="icon-weights-info"
                                        />
                                    </>
                                )
                            }
                            {
                                row.category === 'speedingOverPosted' && (
                                    <>
                                        {`${messages['behavior_' + row.metric + (user.isKph ? 'Kph' : '')]}`}
                                        <Button
                                            type="icon"
                                            icon="info"
                                            style={{ fontSize: '15px' }}
                                            tooltip={messages['behavior_' + row.metric + (user.isKph ? 'Kph' : '')]}
                                            onClick={() => {
                                            }}
                                            className="icon-weights-info"
                                        />
                                    </>
                                )
                            }
                        </span>
                    )
                },
                exportValue: (row) => {
                    if (!['vehicleSpeed', 'speedingOverPosted'].includes(row.category)) {
                        return `${messages['behavior_' + row.metric]}`
                    } else if (['vehicleSpeed'].includes(row.category)) {
                        return `${messages['behavior_' + row.metric]} ${row.metricValue ? (user.isKph ? convertToKPH(row.metricValue) : row.metricValue) : ''}`
                    } else if (['speedingOverPosted'].includes(row.category)) {
                        return `${messages['behavior_' + row.metric + (user.isKph ? 'Kph' : '')]}`
                    }
                },
                functionString: `
                    const metrics = {
                        HardBrake: '${messages['behavior_hardBrake']}',
                        HardTurn: '${messages['behavior_hardTurn']}',
                        RapidAcceleration: '${messages['behavior_rapidAcceleration']}',
                        PotentialIncident: '${messages['behavior_potentialIncident']}',
                        DriverMonitoringSystem: '${messages['behavior_driverMonitoringSystem']}',
                        UsingPhone: '${messages['behavior_usingPhone']}',
                        Smoking: '${messages['behavior_smoking']}',
                        Drowsiness: '${messages['behavior_drowsiness']}',
                        NoSeatbelt: '${messages['behavior_noSeatbelt']}',
                        DayDreaming: '${messages['behavior_dayDreaming']}',
                        Yawn: '${messages['behavior_yawn']}',
                        Asleep: '${messages['behavior_asleep']}',
                        Distracted: '${messages['behavior_distracted']}',
                        CameraObstructed: '${messages['behavior_cameraObstructed']}',
                        IdleNoDriver: '${messages['behavior_idleNoDriver']}',
                        PassengersDetection: '${messages['behavior_passengersDetection']}',
                        AdvancedDriverAssistanceSystem: '${messages['behavior_advancedDriverAssistanceSystem']}',
                        CollisionWarning: '${messages['behavior_forwardCollisionWarning']}',
                        CloseFollowing: '${messages['behavior_closeFollowing']}',
                        Tailgating: '${messages['behavior_tailgating']}',
                        LaneDeparture: '${messages['behavior_laneDeparture']}',
                        StopSignViolation: '${messages['behavior_stopSignViolation']}',
                        speedLabel: '${messages['behavior_speedLabel']}',
                        speedOverLow: '${messages['behavior_speedOverLow']}',
                        speedOverMedium: '${messages['behavior_speedOverMedium']}',
                        speedOverHigh: '${messages['behavior_speedOverHigh']}',
                        speedOverLowKph: '${messages['behavior_speedOverLowKph']}',
                        speedOverMediumKph: '${messages['behavior_speedOverMediumKph']}',
                        speedOverHighKph: '${messages['behavior_speedOverHighKph']}',
                        pedestrianDetection: '${messages['behavior_pedestrianDetection']}',
                    }

                    if (!['SpeedOverPosted', 'SpeedOver'].includes(row?.metric)){
                        return metrics[row?.metric] || row?.metric;
                    } else if (['SpeedOverPosted'].includes(row?.metric)) {
                        if (row.metricValue <= 5) {
                            return metrics['speedOverLow' + '${user.isKph ? 'Kph' : ''}']
                        } else if (row.metricValue <= 10) {
                            return metrics['speedOverMedium' + '${user.isKph ? 'Kph' : ''}']
                        } else if (row.metricValue > 11) {
                            return metrics['speedOverHigh' + '${user.isKph ? 'Kph' : ''}']
                        }
                    } else if (['SpeedOver'].includes(row?.metric)) {
                        let formatKph = row.metricValue;
                        if (${user.isKph}){
                            let kph = 0;
                            if (parseInt(row.metricValue) > 0) {
                                kph = (row.metricValue * ${MILES_TO_KPH});
                            }
                            formatKph =  Math.ceil(kph);
                        }
                        return metrics.speedLabel + formatKph
                    }
                    `
            },
            {
                key: 'points',
                title: messages['behavior_riskFactorsPoints'],
                show: true,
                export: true,
                sort: false,
                width: 250,
                render: (row) => {
                    return (
                        <Slider
                            min={1}
                            max={10}
                            value={row?.points || 1}
                            onChange={(value) => {
                            }}
                            showCenterValue
                        />
                    )
                },
                exportValue: (row) => row?.points || '1',
                functionString: `return row?.points || '1'`
            }
        ]);
    }, [locale, user.isKph]);

    useEffect(() => {
        if (exportResult && exportName === "searchBehaviorPoints") {
            NotificationManager.info(
                messages['exportInProcessBody'],
                messages['exportInProcessTitle'],
                4000,
                null,
                null,
                'info',
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [exportResult, exportName]);

    useEffect(() => {
        if (filter) {
            let conditions = []

            if (filter.categories?.length) {
                conditions = [{
                    fields: 'behaviorMetric_category',
                    terms: filter.categories.map(metric => metric.id)
                }]
            }

            if (filter.metrics?.length) {
                const metrics = filter.metrics.map(metric => {
                    if (metric.id.startsWith(METRIC_SPEED_LABEL)) {
                        return {
                            id: METRIC_SPEED_KEY,
                            value: Number(metric.id.split('-')[1]),
                        }
                    } else if (METRIC_SPEED_OVER_LABEL_LIST.includes(metric.id)) {
                        let value = 0;
                        if (metric.id === METRIC_SPEED_OVER_LABEL_LOW) {
                            value = 5
                        } else if (metric.id === METRIC_SPEED_OVER_LABEL_MEDIUM) {
                            value = 10
                        } else if (metric.id === METRIC_SPEED_OVER_LABEL_HIGH) {
                            value = 15
                        }
                        return {
                            id: METRIC_SPEED_OVER_KEY,
                            value,
                        }
                    } else {
                        return { id: metric.id, value: 0 }
                    }

                })

                conditions = [
                    {
                        fields: 'metric',
                        terms: metrics.map(metric => metric.id)
                    }, {
                        fields: 'metricValue',
                        terms: metrics.map(metric => metric.value)
                    }
                ]
            }

            pagination.addConditions(1, conditions)
            setSelectedItems([])
            tableRef?.current?.setPage(1)
            tableRef?.current?.setSelectedItems([]);
        }
    }, [filter]);

    const updateGradesRequest = () => {
        // small timeout to send the request in order to improve the performance
        return setTimeout(() => {
            preferencesRef?.current?.savePreference("Company", "behaviorView", { scores })
        }, 300);
    }

    const refreshTable = () => {
        pagination.addConditions(1, [])
        setSelectedItems([])
        tableRef?.current?.setPage(1)
        tableRef?.current?.setSelectedItems([]);
    }

    function haveRepeatingNumbers(valor, indice, lista) {
        return !(lista.indexOf(valor) === indice);
    }

    const onChangeSliderScores = (value) => {
        const [valueLow, valueMedium] = value;
        if (valueLow !== 0 && valueMedium !== 100) {
            // to prevent slider with the same value
            if (!value.some(haveRepeatingNumbers)) {
                setMovedSlider(true);
                setScores(value);
            }
        }
    }

    const filteredItems = useMemo(() => 
        (items || []).filter(item => item?.metric !== undefined),
        [items]
    );

    return (
        <Preferences
            ref={preferencesRef}
            preferencesKey="behaviorView"
            onInitialPreferences={(preferences, preferencesCompany) => {

                dispatch(openModalBehaviorAdvanceFilter({
                    hasFilters: preferences?.categories?.length || preferences?.metrics?.length,
                    preference: { categories: preferences?.categories, metrics: preferences?.metrics }
                }))


                if (preferencesCompany?.scores) {
                    let newScores = preferencesCompany?.scores

                    if (newScores) {
                        setScores(newScores);
                    }
                }
            }}
        >
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={6} sm={2}>
                    <Grid
                        container
                        direction="row"
                        justifyContent="flex-start"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item style={{ display: 'flex' }}>
                            <Button
                                type="icon"
                                id="buttonFilterBehavior"
                                icon="filter_list"
                                tooltip={messages['table_filter']}
                                className={hasFilters ? 'select-view-icon-filter' : 'select-view-icon'}
                                onClick={() => dispatch(openModalBehaviorAdvanceFilter({ open: !openModalAdvanceFilterMetric }))}
                            />
                            {editablePermissions &&
                                <IconButton
                                    tooltip={messages["add"]}
                                    placement="bottom"
                                    variant="secondary"
                                    icon="add_box"
                                    size="small"
                                    disabled={loading}
                                    onClick={() => dispatch(openModalCreateUpdateMetricService())}
                                />
                            }
                            <TableButtons
                                tableRef={tableRef}
                                buttons={["export"]}
                                onExport={(type) => {
                                    if (selectedItems?.length) {
                                        if (type === "Csv") {
                                            tableRef.current.exportCSV();
                                        } else if (type === "Pdf") {
                                            tableRef.current.exportPDF();
                                        }
                                    } else {
                                        let currentConditions = pagination.currentConditions;
                                        let currentSort = pagination.currentSort ?? [
                                            {
                                                "field": "behaviorMetric_category",
                                                "order": "ASC"
                                            },
                                            {
                                                "field": "metric",
                                                "order": "ASC"
                                            },
                                            {
                                                "field": "metricValue",
                                                "order": "ASC"
                                            }
                                        ];
                                        const columns = pagination?.columns
                                        const headers = columns.filter(x => x.show && x.export).map(x => {
                                            return { header: x.title, column: x.key, functionString: x.functionString }
                                        }) || [];

                                        dispatch(sendGenericExport(
                                            "searchBehaviorPoints",
                                            type,
                                            `query searchBehaviorPoints ($conditions: [behaviorPointSearchInput], $sort: [behaviorPointSortInput], $limit: Int, $offset: Int) {
                                                           searchBehaviorPoints (conditions: $conditions, sort: $sort, limit: $limit, offset: $offset) {
                                                                items {
                                                                    metric
                                                                    metricValue
                                                                    points
                                                                    behaviorMetric{
                                                                        category
                                                                    }
                                                                    id
                                                                }
                                                                total
                                                            }
                                                        }`,
                                            {
                                                limit: 10,
                                                offset: 0,
                                                conditions: currentConditions,
                                                sort: currentSort,
                                            },
                                            headers,
                                            { unit: 'px', size: 'A4', orientation: 'landscape', fontSize: 10 },
                                            [],
                                            false,
                                            true
                                        ));
                                    }
                                }}
                            />
                            {editablePermissions &&
                                <IconButton
                                    tooltip={messages["scheduleVehicleService_delete_button_bar"]}
                                    placement="bottom"
                                    variant="secondary"
                                    icon="delete"
                                    size="small"
                                    onClick={() => {
                                        dispatch(openModalDeleteMetricService(selectedItems))
                                    }}
                                    style={{ opacity: !selectedItems.length && '0.6' }}
                                    isDisabled={!selectedItems.length}
                                />
                            }
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
            <div className='container-pointer-header'>
                <strong className='label-pointer-header'>
                    {messages['behavior_riskFactors']}
                    <Button
                        type="icon"
                        icon="info"
                        style={{ fontSize: '15px' }}
                        tooltip={messages['behavior_tooltip_riskFactors']}
                        onClick={() => {
                        }}
                        className="icon-weights-info"
                    />
                </strong>
                <Slider
                    isMultiple={true}
                    min={0}
                    max={100}
                    colorOne={COLORS_SLIDERS_SCORE[0]}
                    colorTwo={COLORS_SLIDERS_SCORE[2]}
                    colorThree={COLORS_SLIDERS_SCORE[4]}
                    value={scores}
                    onChange={onChangeSliderScores}
                    showCenterValue
                />
            </div>
            <TableView
                ref={tableRef}
                keyColumn="id"
                data={filteredItems}
                total={total}
                loading={loading}
                messageNoData={messages['behavior_tableNoData']}
                hideAllInPagination
                topheight={300}
                hideDragable
                exportName={`searchBehaviorPoints`}
                onSelectedItems={(items) => setSelectedItems(items)}
                {...pagination}
            />
            {openModalCreateUpdateMetric && <CreateUpdateMetric refresh={() => refreshTable()} />}
            {openModalDeleteMetric && <DeleteMetric refresh={() => refreshTable()} />}
            {<AdvanceFilterMetric preferencesRef={preferencesRef} />}
        </Preferences>
    );
};

export default BehaviorConfigView;
